import React, { Fragment, useState, useEffect, useMemo } from 'react'
import classnames from 'classnames'
import {
  makeStyles,
  CircularProgress,
  Typography,
  IconButton,
  OutlinedInput,
  Select,
  MenuItem,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { CameraIcon } from '../../../../../Assets/Images/icons/camera'
import { styles } from './styles'
import {
  validateUserProfile,
  PROFILE_FORM_INPUTS,
  handleImageUpdate,
  requiredFields,
} from './utils'
import { ROLE_CLIENT, ROLE_CLIENT_ADMIN } from '../../../../../shared/constants'
import classNames from 'classnames'

const useStyles = makeStyles(() => styles)

export const ProfileEditForm = ({
  user,
  handleSave,
  saving,
  setEdit,
  edit,
}) => {
  const classes = useStyles()
  const loading = !Object.values(user).length
  const history = useHistory()
  const [userData, setUserData] = useState(user ? { ...user } : {})
  const [errors, setErrors] = useState({})
  const [profilePicture, setProfilePicture] = useState(user.profilePicture)

  const disableSave = useMemo(
    () =>
      !requiredFields.every(inputName => !!userData[inputName]) ||
      Object.values(errors).some(value => !!value),
    [errors, userData],
  )

  const hiddenFileInput = React.useRef(null)

  useEffect(() => {
    if (!loading) {
      setUserData({ ...user })
      setProfilePicture(user.profilePicture)
    }
  }, [user, loading])

  const handleImageButtonClick = event => {
    hiddenFileInput.current.click()
  }

  const handleFormChange = e => {
    validateUserProfile(e, setErrors, errors)
    setUserData({ ...userData, [e.target.name]: e.target.value })
  }

  return loading || saving ? (
    <div className={classes.loadingContainer}>
      <CircularProgress />
    </div>
  ) : (
    <Fragment>
      <div
        className={classNames(
          classes.infoContainer,
          'makeStyles-infoContainer',
        )}
      >
        <div
          className={classNames(classes.imageColumn, 'makeStyles-imageColumn')}
        >
          <div className={classes.imageContainer} cypress_id={'userAvatar'}>
            <img
              className={classNames(classes.image, 'makeStyles-image')}
              src={profilePicture}
              alt="profile"
            />
            <IconButton
              className={classes.cameraIcon}
              onClick={handleImageButtonClick}
            >
              <CameraIcon />
              <input
                type="file"
                accept=".jpg, .jpeg, .png"
                ref={hiddenFileInput}
                onChange={event =>
                  handleImageUpdate({
                    event,
                    setUserData,
                    userData,
                    setProfilePicture,
                  })
                }
                style={{ display: 'none' }}
              />
            </IconButton>
          </div>
        </div>
        <div className={classes.info}>
          <div className={classNames(classes.row,'makeStyles-row')}>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                First name
              </Typography>
              <OutlinedInput
                className={classes.value}
                value={userData.firstName}
                name={PROFILE_FORM_INPUTS.FIRST_NAME}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.FIRST_NAME]}
                cypress_id={'firstName'}
              />
              {errors[PROFILE_FORM_INPUTS.FIRST_NAME] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.FIRST_NAME]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Last name
              </Typography>
              <OutlinedInput
                className={classes.value}
                value={userData.lastName}
                name={PROFILE_FORM_INPUTS.LAST_NAME}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.LAST_NAME]}
                cypress_id={'lastName'}
              />
              {errors[PROFILE_FORM_INPUTS.LAST_NAME] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.LAST_NAME]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>Role</Typography>

              <Select
                value={userData.role}
                defaultValue={userData.role}
                name={PROFILE_FORM_INPUTS.ROLE}
                onChange={handleFormChange}
                variant="outlined"
                className={classes.value}
                cypress_id={'userRole'}
              >
                <MenuItem value="ROLE_CLIENT" className={classes.value}>
                  User
                </MenuItem>
                <MenuItem value="CLIENT_ADMIN" className={classes.value}>
                  Admin
                </MenuItem>
              </Select>

              {errors[PROFILE_FORM_INPUTS.ROLE] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.ROLE]}
                </span>
              )}
            </div>
          </div>

          <div className={classNames(classes.row,'makeStyles-row')}>
            {[ROLE_CLIENT_ADMIN, ROLE_CLIENT].includes(user.role) ? null : (
              <div
                className={classnames(classes.infoItem, 'makeStyles-infoItem')}
              >
                <Typography className={classes.infoItemTitle}>
                  Country
                </Typography>
                <Typography className={classes.value}>
                  {userData.countryName}
                </Typography>
              </div>
            )}

            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>Company</Typography>
              <OutlinedInput
                className={classes.value}
                value={(userData.client && userData.client.name) || '-'}
                disabled
                cypress_id={'companyName'}
              />
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            />
          </div>

          <div className={classNames(classes.row,'makeStyles-row')}>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Email address
              </Typography>
              <OutlinedInput
                className={classes.value}
                value={userData.email}
                name={PROFILE_FORM_INPUTS.EMAIL}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.EMAIL]}
                disabled
                cypress_id={'emailAddress'}
              />
              {errors[PROFILE_FORM_INPUTS.EMAIL] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.EMAIL]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Business phone number
              </Typography>
              <OutlinedInput
                className={classes.value}
                value={userData.mobilePhone}
                name={PROFILE_FORM_INPUTS.PHONE}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.PHONE]}
                cypress_id={'businessPhoneNumber'}
              />
              {errors[PROFILE_FORM_INPUTS.PHONE] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.PHONE]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            />
          </div>
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <button
          className={classes.profileButton}
          onClick={() => {
            setEdit(!edit)
            history.goBack()
          }}
        >
          Cancel
        </button>
        <button
          className={classnames(classes.profileButton, classes.saveButton, {
            [classes.disabled]: disableSave,
          })}
          onClick={() => {
            handleSave(userData)
          }}
          disabled={disableSave}
        >
          Save
        </button>
      </div>
    </Fragment>
  )
}
